"use client";

import { useEffect, useRef, useState } from "react";
import { Minus, Moon, Plus, Sun } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import {
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
  clampFontSize,
  type ReadingFont,
  type ReadingLeading,
  type ReadingPrefs,
  type ReadingTheme,
} from "@/lib/reading-prefs";

const THEME_OPTIONS: { value: ReadingTheme; label: string; swatch: string }[] =
  [
    { value: "paper", label: "Paper", swatch: "#fafcfd" },
    { value: "sepia", label: "Sepia", swatch: "#f3ead8" },
    { value: "dark", label: "Dark", swatch: "#131313" },
  ];

const FONT_OPTIONS: {
  value: ReadingFont;
  label: string;
  fontFamily: string;
}[] = [
  {
    value: "literata",
    label: "Literata",
    fontFamily: "var(--font-literata), Georgia, serif",
  },
  { value: "inter", label: "Inter", fontFamily: "var(--font-inter), sans-serif" },
  {
    value: "mono",
    label: "Mono",
    fontFamily: "var(--font-jetbrains-mono), ui-monospace, monospace",
  },
];

const LEADING_OPTIONS: { value: ReadingLeading; label: string; gap: number }[] =
  [
    { value: "compact", label: "Compact spacing", gap: 2.5 },
    { value: "normal", label: "Normal spacing", gap: 3.5 },
    { value: "relaxed", label: "Relaxed spacing", gap: 4.5 },
  ];

function LeadingGlyph({ gap }: { gap: number }) {
  return (
    <svg width="18" height="16" viewBox="0 0 18 16" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={i}
          x="1"
          y={1.5 + i * gap}
          width="16"
          height="1.6"
          rx="0.8"
          fill="currentColor"
        />
      ))}
    </svg>
  );
}

export default function ReadingControls({
  prefs,
  onChange,
}: {
  prefs: ReadingPrefs;
  onChange: (patch: Partial<ReadingPrefs>) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    panelRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !panelRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isOpen]);

  const rowLabelClass =
    "text-[11px] uppercase tracking-[0.14em] text-[var(--reading-muted)]";
  const optionClass =
    "grid h-10 place-items-center rounded-xl border transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reading-heading)]";
  const selectedClass =
    "border-[var(--reading-heading)] text-[var(--reading-heading)]";
  const unselectedClass =
    "border-[var(--reading-border)] text-[var(--reading-muted)] hover:border-[var(--reading-muted)]";

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Reading settings"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-full border border-[var(--reading-border)] bg-[var(--reading-bg)] px-5 py-2.5 text-[17px] font-semibold text-[var(--reading-heading)] shadow-[0_8px_24px_rgba(0,0,0,0.14)] transition-colors hover:border-[var(--reading-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reading-heading)]"
        style={{ fontFamily: "var(--font-literata), Georgia, serif" }}
      >
        Aa
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-label="Reading settings"
            tabIndex={-1}
            className="fixed bottom-20 left-1/2 z-[60] max-h-[calc(100dvh-6.5rem)] w-[min(20.5rem,calc(100vw-2rem))] -translate-x-1/2 space-y-5 overflow-y-auto rounded-2xl border border-[var(--reading-border)] bg-[var(--reading-bg)] p-5 text-[var(--reading-heading)] shadow-[0_20px_50px_rgba(0,0,0,0.22)] focus-visible:outline-none"
            initial={
              shouldReduceMotion ? false : { opacity: 0, y: 12, scale: 0.97 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              shouldReduceMotion
                ? { opacity: 0 }
                : { opacity: 0, y: 8, scale: 0.97 }
            }
            transition={{ duration: shouldReduceMotion ? 0 : 0.18 }}
          >
            {/* Theme */}
            <div>
              <p className={rowLabelClass}>Theme</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {THEME_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange({ theme: option.value })}
                    aria-pressed={prefs.theme === option.value}
                    className={`${optionClass} h-12 flex-col gap-1 ${
                      prefs.theme === option.value
                        ? selectedClass
                        : unselectedClass
                    }`}
                  >
                    <span
                      className="h-4 w-4 rounded-full border border-black/15"
                      style={{ backgroundColor: option.swatch }}
                      aria-hidden="true"
                    />
                    <span className="text-[11px]">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Warmth */}
            <div>
              <p className={rowLabelClass}>Warmth</p>
              <div className="mt-2 flex items-center gap-3">
                <Moon
                  className="h-4 w-4 shrink-0 text-[var(--reading-muted)]"
                  aria-hidden="true"
                />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={prefs.warmth}
                  onChange={(event) =>
                    onChange({ warmth: Number(event.target.value) })
                  }
                  aria-label="Screen warmth"
                  className="reading-warmth-slider w-full"
                />
                <Sun
                  className="h-4 w-4 shrink-0 text-[#e8871e]"
                  aria-hidden="true"
                />
              </div>
            </div>

            {/* Font */}
            <div>
              <p className={rowLabelClass}>Font</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {FONT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange({ font: option.value })}
                    aria-pressed={prefs.font === option.value}
                    aria-label={`${option.label} font`}
                    className={`${optionClass} h-12 flex-col gap-0.5 ${
                      prefs.font === option.value
                        ? selectedClass
                        : unselectedClass
                    }`}
                  >
                    <span
                      className="text-[15px] leading-none"
                      style={{ fontFamily: option.fontFamily }}
                      aria-hidden="true"
                    >
                      Aa
                    </span>
                    <span className="text-[10px]">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div>
              <p className={rowLabelClass}>Size</p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onChange({ fontSize: clampFontSize(prefs.fontSize - 1) })
                  }
                  disabled={prefs.fontSize <= MIN_FONT_SIZE}
                  aria-label="Decrease font size"
                  className={`${optionClass} w-full ${unselectedClass} disabled:cursor-not-allowed disabled:opacity-35`}
                >
                  <span className="flex items-center gap-0.5">
                    <span className="text-[13px]">A</span>
                    <Minus className="h-3 w-3" aria-hidden="true" />
                  </span>
                </button>
                <span
                  aria-live="polite"
                  className="w-14 shrink-0 text-center text-[13px] text-[var(--reading-muted)]"
                >
                  {prefs.fontSize}px
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onChange({ fontSize: clampFontSize(prefs.fontSize + 1) })
                  }
                  disabled={prefs.fontSize >= MAX_FONT_SIZE}
                  aria-label="Increase font size"
                  className={`${optionClass} w-full ${unselectedClass} disabled:cursor-not-allowed disabled:opacity-35`}
                >
                  <span className="flex items-center gap-0.5">
                    <span className="text-[17px]">A</span>
                    <Plus className="h-3 w-3" aria-hidden="true" />
                  </span>
                </button>
              </div>
            </div>

            {/* Spacing */}
            <div>
              <p className={rowLabelClass}>Spacing</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {LEADING_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange({ leading: option.value })}
                    aria-pressed={prefs.leading === option.value}
                    aria-label={option.label}
                    className={`${optionClass} ${
                      prefs.leading === option.value
                        ? selectedClass
                        : unselectedClass
                    }`}
                  >
                    <LeadingGlyph gap={option.gap} />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
